<template>
    <v-app>
        <div class="my-0 py-0">
            <v-card v-if="bom.id" :loading="loading" height="100vh">
                <v-card-title>
                    <h2 class="font-weight-regular">
                        {{ process }}
                        <span class="font-weight-black"
                            >{{ bom.code }} | {{ bom.project.reference }}
                            {{ bom.project.name }}</span
                        >
                    </h2>
                    <v-spacer />
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <v-container
                        fluid
                        ref="container"
                        class="px-0 mt-1 pb-0 mb-0"
                    >
                        <v-row no-gutters ref="details">
                            <v-col cols="4" class="pl-0 pt-2">
                                <div>
                                    <p class="text-capitalize">
                                        <strong>STATUS: </strong>
                                        {{ ` ${bom.status}` }}
                                    </p>
                                    <p class="text-capitalize">
                                        <strong>CREATED BY: </strong>
                                        {{
                                            ` ${
                                                bom.creator && bom.creator.name
                                                    ? bom.creator.name
                                                    : bom.createdBy
                                            }`
                                        }}
                                    </p>
                                    <p class="text-capitalize">
                                        <strong>PROJECT MANAGER: </strong>
                                        {{
                                            bom.projectManager &&
                                            bom.projectManager.name
                                                ? bom.projectManager.name
                                                : bom.projectManager
                                        }}
                                    </p>
                                </div>
                            </v-col>
                            <v-col cols="4" class="pl-0 pt-2">
                                <div>
                                    <p>
                                        <strong>CREATED: </strong
                                        >{{
                                            ` ${formatDate(
                                                bom.createdOn.seconds ||
                                                    bom.createdOn._seconds
                                            )}`
                                        }}
                                    </p>
                                    <v-textarea
                                        v-model="bom.notes"
                                        prepend-icon="mdi-text"
                                        prefix="Notes: "
                                        hide-details
                                        rows="1"
                                        class="pt-0"
                                        readonly
                                    >
                                    </v-textarea>
                                </div>
                            </v-col>
                            <v-spacer />
                            <v-col cols="4" class="d-flex justify-end mb-3">
                                <canvas id="BOMqr" @click="copyQRLink"></canvas>
                            </v-col>
                        </v-row>
                    </v-container>
                    <BOMItems
                        :bom="bom"
                        :itemsHeight="itemsHeight"
                        :readBOMOnly="true"
                        :partialDelivery="false"
                        @closeProcessBOM="close"
                    />
                </v-card-text>
            </v-card>
            <!--Error Alert-->
            <Errors />
            <!--ALERT MESSAGE-->
            <v-alert
                v-if="activateAlert"
                :type="alertMessage.includes('Error') ? 'error' : 'success'"
                :color="alertMessage.includes('Error') ? 'error' : 'primary'"
                :style="{
                    position: 'absolute',
                    right: '0px',
                    bottom: '0px',
                    zIndex: 9999,
                }"
            >
                <v-row no-gutters>
                    <v-col cols="11">
                        <p class="my-0">{{ alertMessage }}</p>
                    </v-col>
                    <v-col cols="1">
                        <v-btn
                            small
                            icon
                            class="mt-n1"
                            @click="activateAlert = false"
                        >
                            <v-icon>
                                mdi-close
                            </v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-alert>
        </div>
    </v-app>
</template>

<script>
import moment from 'moment'
import qrious from 'qrious'
import API from '@/services/api'

export default {
    name: 'PublicProcessBOM',
    props: {
        bomId: {
            type: String,
            required: true,
        },
    },
    components: {
        BOMItems: () => import('@/components/WorkOrders/BOMItems'),
        Errors: () => import('@/components/Layout/Errors'),
    },
    data: () => ({
        loading: false,
        height: 0,
        itemsHeight: 0,
        bom: {},
        process: 'BOM',
        qrLink: '',
        activateAlert: false,
        alertMessage: '',
        user: {},
    }),
    async mounted() {
        try {
            this.loading = true
            const companyId = this.$route.query.companyId

            // Check if user is anonymous
            try {
                const {
                    data: { user },
                } = await API.getMyInfo()
                this.user = user
            } catch (error) {
                // User is anonymous
            }

            // Pass companyId if user is anonymous and it's available in query
            const companyIdForRequest =
                this.user?.name === 'AnonymousSession' && companyId
                    ? companyId
                    : undefined

            this.bom = await API.getBOMById({
                bomId: this.bomId,
                companyId: companyIdForRequest,
            })
            // Include companyId in QR link if available
            const qrLinkCompanyId = companyId ? `?companyId=${companyId}` : ''
            this.qrLink = `${process.env.VUE_APP_CLIENT_HOST}/public-bom/${this.bom.id}${qrLinkCompanyId}`
            // Wait for DOM to render before accessing refs
            await this.$nextTick()
            // qr code
            new qrious({
                element: document.getElementById('BOMqr'),
                value: this.qrLink,
                size: 150,
            })
            this.onResize()
        } catch (error) {
            console.error('Error loading BOM:', error)
            // Show error message to user
            this.activateAlert = true
            this.alertMessage = `Error: ${error.message ||
                'Failed to load BOM'}`
        } finally {
            this.loading = false
        }
    },
    methods: {
        close() {
            // No-op for public view
        },
        onResize() {
            // Check if refs exist before accessing them
            if (
                this.$refs.container &&
                this.$refs.container.clientHeight &&
                this.$refs.details &&
                this.$refs.details.clientHeight
            ) {
                const containerHeight = this.$refs.container.clientHeight
                const detailsHeight = this.$refs.details.clientHeight
                this.height =
                    window.innerHeight - containerHeight - detailsHeight + 50
                this.itemsHeight = window.innerHeight - 345
            }
        },
        formatDate(seconds) {
            return `${moment.unix(seconds).format('L')} ${moment
                .unix(seconds)
                .format('LT')}`
        },
        async copyQRLink() {
            try {
                await navigator.clipboard.writeText(this.qrLink)
                this.activateAlert = true
                this.alertMessage = 'Link copied to clipboard'
            } catch (error) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea')
                textArea.value = this.qrLink
                textArea.style.position = 'fixed'
                textArea.style.left = '-999999px'
                document.body.appendChild(textArea)
                textArea.select()
                try {
                    document.execCommand('copy')
                    this.activateAlert = true
                    this.alertMessage = 'Link copied to clipboard'
                } catch (err) {
                    console.error('Failed to copy link', err)
                }
                document.body.removeChild(textArea)
            }
        },
    },
}
</script>

<style></style>
